/** Pure rules and wording for the column-mapping step. No React, no server
 *  calls — testable in isolation. */
import type { ExpectedField } from '@discovr/types';
import { MAIN_DELIVERABLE_GROUP, USAGE_RIGHTS_DAYS_KEY, USAGE_RIGHTS_KEY } from './importWizardHelpers';

const PRICE_SUFFIX = ' (₹)';
const REQUIRED_SUFFIX = ' *';

/** Mirrors the server's 400 for the same mapping, so the admin is stopped
 *  here instead of after the round-trip. */
export const USAGE_DAYS_NEED_PRICE_COLUMN_MESSAGE =
  'Map the Usage Rights price column as well: days need a price in the same row.';
export const NO_MAIN_DELIVERABLE_TITLE = 'No main deliverable column is matched.';
export const NO_MAIN_DELIVERABLE_BODY =
  'Creators who are new to this campaign need at least one main deliverable price to be imported.';

function mappedFieldKeys(mapping: Record<string, string>): Set<string> {
  return new Set(Object.values(mapping).filter(Boolean));
}

function isMainDeliverable(field: ExpectedField): boolean {
  return field.group === MAIN_DELIVERABLE_GROUP;
}

/** A field's text in the "Maps to" dropdown: the server's label as sent, a
 *  currency cue on price fields, then the required marker. */
export function mappingOptionLabel(field: ExpectedField): string {
  const price = field.isDeliverable ? PRICE_SUFFIX : '';
  const required = field.required ? REQUIRED_SUFFIX : '';
  return `${field.label}${price}${required}`;
}

/** Labels of the optional fields no column is mapped to, in field order.
 *  Required ones are left out: they are reported as missing instead. */
export function unmappedOptionalFieldLabels(expectedFields: ExpectedField[], mapping: Record<string, string>): string[] {
  const mappedKeys = mappedFieldKeys(mapping);
  return expectedFields.filter((f) => !f.required && !mappedKeys.has(f.key)).map((f) => f.label);
}

/** A days column with no Usage Rights price column: every row's days would
 *  have no price to belong to. */
export function usageDaysMappedWithoutPrice(mapping: Record<string, string>): boolean {
  const mappedKeys = mappedFieldKeys(mapping);
  return mappedKeys.has(USAGE_RIGHTS_DAYS_KEY) && !mappedKeys.has(USAGE_RIGHTS_KEY);
}

/** True when the campaign has main deliverables but no column maps to any of
 *  them. A field list with no main deliverable (a legacy batch) never warns:
 *  there would be nothing the admin could map to clear it. */
export function isMainDeliverableUnmapped(expectedFields: ExpectedField[], mapping: Record<string, string>): boolean {
  const mainFields = expectedFields.filter(isMainDeliverable);
  if (mainFields.length === 0) return false;
  const mappedKeys = mappedFieldKeys(mapping);
  return !mainFields.some((f) => mappedKeys.has(f.key));
}
